/*
Copyright (C) 2024 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR

This file is part of CienciaNaEscola. CienciaNaEscola is free software: you can redistribute it and/or modify it under the terms of the GNU
General Public License as published by the Free Software Foundation, either version 3 of the License, or (at your option) any later version.
CienciaNaEscola is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU General Public License for more details. You should have received a copy
of the GNU General Public License along with CienciaNaEscola.  If not, see <https://www.gnu.org/licenses/>
*/

import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
//import { serialize } from 'object-to-formdata';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { AlertContext } from '../contexts/AlertContext';
import MaterialSymbol from './MaterialSymbol';
import { Tooltip } from 'bootstrap';

const CreateProtocolPropertiesStyles = `
    .create-page-custom-scroll::-webkit-scrollbar {
        width: 10px;
    }

    .create-page-custom-scroll::-webkit-scrollbar-track {
        background: #53535360;
        border-radius: 16px;
    }

    .create-page-custom-scroll::-webkit-scrollbar-thumb {
        background: #53535390;
        border-radius: 16px;  /* Rounded corners for the thumb */
    }

    .create-page-custom-scroll::-webkit-scrollbar-thumb:hover {
        background: #535353;
    }

    .search-input:focus,
    .search-input:active {
        box-shadow: inset 0px 3px 3px 0px #00000040;
    }

    .filter-pill-btn {
        transition: all 0.2s ease-in-out;
    }

    .filter-pill-btn:hover {
        opacity: 1 !important;
        box-shadow: inset 0 0 0 100px rgba(0, 0, 0, 0.2) !important;
    }

    .filter-pill-btn:active {
        filter: brightness(0.8);
        box-shadow: inset 0 0 0 100px rgba(0, 0, 0, 0.3), inset 0px 4px 4px 0px rgba(0, 0, 0, 0.25) !important;
    }

    .form-select:hover {
        cursor: pointer;
    }

    .animate-fade-in { animation: fadeIn 0.1s ease-in; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

/**
 * Componente responsável por gerenciar as propriedades de um protocolo.
 * @param {Object} props - Propriedades do componente.
 * @param {Object} props.searchedOptions - Objeto contendo as opções pesquisadas.
 * @param {Object} props.protocol - Objeto representando o protocolo atual.
 * @param {Function} props.setProtocol - Função para atualizar o estado do protocolo.
 * @param {Function} props.setSearchInputs - Função para atualizar os inputs de pesquisa.
 * @param {Object} props.searchInputs - Objeto contendo os valores atuais dos inputs de pesquisa.
 */
function CreateProtocolProperties(props) {
    const { searchedOptions, protocol, setProtocol, setSearchInputs, searchInputs } = props;

    const { user } = useContext(AuthContext);
    const { showAlert } = useContext(AlertContext);

    const [apiData, setApiData] = useState({ users: [], classrooms: [] });
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const [internalControl, setInternalControl] = useState({
        manualViewersUser: [],
        excludedViewersUser: [],
        manualAnswersViewersUser: [],
        excludedAnswersViewersUser: [],
        manualAppliers: [],
        excludedAppliers: [],
    });

    const masterPool = useMemo(() => {
        const merge = (apiList, searchedList, isClassroom = false) => {
            const key = isClassroom ? 'name' : 'username';
            const map = new Map();
            (apiList || []).forEach((item) => map.set(item.id, item));
            (searchedList || []).forEach((item) => {
                if (!map.has(item.id)) map.set(item.id, item);
            });
            return Array.from(map.values()).sort((a, b) => (a[key] || '').localeCompare(b[key] || ''));
        };

        return {
            users: merge(apiData.users, [
                ...searchedOptions.viewersUser,
                ...searchedOptions.answersViewersUser,
                ...searchedOptions.appliers,
            ]),
            classrooms: merge(apiData.classrooms, [...searchedOptions.viewersClassroom, ...searchedOptions.answersViewersClassroom], true),
        };
    }, [apiData, searchedOptions]);

    const filteredLists = useMemo(() => {
        const filterFn = (list, term) => {
            if (!term) return list;
            const t = term.toLowerCase();
            return list.filter((i) => (i.username || i.name || '').toLowerCase().includes(t));
        };

        return {
            viewersUser: filterFn(masterPool.users, searchInputs.viewersUser),
            answersViewersUser: filterFn(masterPool.users, searchInputs.answersViewersUser),
            appliers: filterFn(
                masterPool.users.filter((u) => u.role !== 'USER' && u.role !== 'ADMIN'),
                searchInputs.appliers
            ),
            viewersClassroom: filterFn(masterPool.classrooms, searchInputs.viewersClassroom),
            answersViewersClassroom: filterFn(masterPool.classrooms, searchInputs.answersViewersClassroom),
        };
    }, [masterPool, searchInputs]);

    const refreshFinalUserList = useCallback(
        (target, currentManualIds, currentClassroomIds, currentExcludedIds) => {
            const userKey = target === 'appliers' ? 'appliers' : target;
            const suffix = target.charAt(0).toUpperCase() + target.slice(1);
            const classroomKey = target === 'viewersUser' ? 'viewersClassroom' : 'answersViewersClassroom';

            const manualIds = currentManualIds || internalControl[`manual${suffix}`] || [];
            const excludedIds = currentExcludedIds || internalControl[`excluded${suffix}`] || [];
            const classroomIds = currentClassroomIds || protocol[classroomKey] || [];
            const classroomList = masterPool.classrooms;

            let groupUserIds = new Set();
            classroomIds.forEach((cId) => {
                const group = classroomList.find((g) => g.id === cId);
                if (group && group.users) group.users.forEach((u) => groupUserIds.add(u.id));
            });

            let finalIds = new Set();
            groupUserIds.forEach((id) => {
                if (!excludedIds.includes(id)) finalIds.add(id);
            });
            manualIds.forEach((id) => finalIds.add(id));

            setProtocol((prev) => ({ ...prev, [userKey]: Array.from(finalIds) }));
        },
        [internalControl, protocol, masterPool.classrooms, setProtocol]
    );

    useEffect(() => {
        const loadData = async () => {
            if (!user?.token) return;
            try {
                const [uRes, cRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}api/user/getVisibleUsers`, {
                        headers: { Authorization: `Bearer ${user.token}` },
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}api/classroom/getVisibleClassrooms`, {
                        headers: { Authorization: `Bearer ${user.token}` },
                    }),
                ]);
                setApiData({ users: uRes.data.data, classrooms: cRes.data.data });
                setIsDataLoaded(true);
            } catch (e) {
                showAlert({ headerText: 'Erro', bodyText: e.message });
            }
        };
        loadData();
    }, [user?.token, showAlert]);

    useEffect(() => {
        if (isDataLoaded) {
            const getManualInitial = (finalIds, selectedClassroomIds, classroomList) => {
                if (!finalIds || finalIds.length === 0) return [];

                // Pegamos todos os IDs que já são fornecidos pelos grupos selecionados
                let idsFromGroups = new Set();
                (selectedClassroomIds || []).forEach((cId) => {
                    const group = classroomList.find((g) => g.id === cId);
                    if (group && group.users) group.users.forEach((u) => idsFromGroups.add(u.id));
                });

                // Os "manuais" são os IDs presentes no protocolo que NÃO estão cobertos pelos grupos
                return finalIds.filter((id) => !idsFromGroups.has(id));
            };

            setInternalControl({
                manualViewersUser: getManualInitial(protocol.viewersUser, protocol.viewersClassroom, masterPool.classrooms),
                excludedViewersUser: [],
                manualAnswersViewersUser: getManualInitial(
                    protocol.answersViewersUser,
                    protocol.answersViewersClassroom,
                    masterPool.classrooms
                ),
                excludedAnswersViewersUser: [],
                manualAppliers: protocol.appliers || [], // Appliers geralmente não têm grupos nesta lógica
                excludedAppliers: [],
            });
        }
        // Rodamos apenas uma vez quando os dados da API e Pool estão prontos
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDataLoaded]);

    useEffect(() => {
        const tooltipList = [];

        const selectors = [
            '.title-tooltip',
            '.description-tooltip',
            '.replicable-tooltip',
            '.visibility-tooltip',
            '.applicability-tooltip',
            '.answer-visiblity-tooltip',
        ];

        selectors.forEach((selector) => {
            const elements = document.querySelectorAll(selector);
            elements.forEach((el) => {
                tooltipList.push(new Tooltip(el, { trigger: 'hover' }));
            });
        });

        return () => {
            tooltipList.forEach((tooltip) => tooltip.dispose());
        };
    }, []);

    const toggleUserSelection = (id, target) => {
        const suffix = target.charAt(0).toUpperCase() + target.slice(1);
        const manualKey = `manual${suffix}`;
        const excludedKey = `excluded${suffix}`;
        const classroomKey = target === 'viewersUser' ? 'viewersClassroom' : 'answersViewersClassroom';

        const isFromGroup = (protocol[classroomKey] || []).some((cId) =>
            masterPool.classrooms.find((g) => g.id === cId)?.users?.some((u) => u.id === id)
        );

        setInternalControl((prev) => {
            let next = { ...prev };
            if (isFromGroup) {
                // Se clicou num cara que veio de grupo, ou ele entra na lista de "Excluídos" ou sai dela
                next[excludedKey] = prev[excludedKey].includes(id) ? prev[excludedKey].filter((e) => e !== id) : [...prev[excludedKey], id];
                next[manualKey] = prev[manualKey].filter((m) => m !== id);
            } else {
                // Seleção manual pura
                next[manualKey] = prev[manualKey].includes(id) ? prev[manualKey].filter((m) => m !== id) : [...prev[manualKey], id];
                next[excludedKey] = prev[excludedKey].filter((e) => e !== id);
            }
            refreshFinalUserList(target, next[manualKey], null, next[excludedKey]);
            return next;
        });
    };

    const toggleClassroom = (id, target) => {
        const currentGroups = protocol[target] || [];
        const isRemoving = currentGroups.includes(id);
        const newGroups = isRemoving ? currentGroups.filter((g) => g !== id) : [...currentGroups, id];

        const userTarget = target === 'viewersClassroom' ? 'viewersUser' : 'answersViewersUser';
        const excludedKey = `excluded${userTarget.charAt(0).toUpperCase() + userTarget.slice(1)}`;

        setInternalControl((prev) => {
            let next = { ...prev };
            if (isRemoving) {
                // Ao remover o grupo, limpamos as exclusões específicas desse grupo
                const groupUsers = masterPool.classrooms.find((g) => g.id === id)?.users?.map((u) => u.id) || [];
                next[excludedKey] = prev[excludedKey].filter((eId) => !groupUsers.includes(eId));
            }
            // Atualiza o protocolo com os novos grupos primeiro
            setProtocol((p) => ({ ...p, [target]: newGroups }));
            refreshFinalUserList(userTarget, null, newGroups, next[excludedKey]);
            return next;
        });
    };

    const bulkAction = (action, target) => {
        const suffix = target.charAt(0).toUpperCase() + target.slice(1);
        const manualKey = `manual${suffix}`;
        const excludedKey = `excluded${suffix}`;

        if (action === 'CLEAR') {
            const nextControl = { ...internalControl, [manualKey]: [], [excludedKey]: [] };
            setInternalControl(nextControl);
            setProtocol((prev) => {
                const nextProtocol = { ...prev, [target]: [] };
                if (target !== 'appliers') {
                    const gKey = target === 'viewersUser' ? 'viewersClassroom' : 'answersViewersClassroom';
                    nextProtocol[gKey] = [];
                }
                refreshFinalUserList(
                    target.includes('User') ? target : target === 'viewersClassroom' ? 'viewersUser' : 'answersViewersUser',
                    [],
                    [],
                    []
                );
                return nextProtocol;
            });
        } else if (action === 'ALL') {
            const ids = filteredLists[target].map((i) => i.id);
            if (target.includes('User') || target === 'appliers') {
                setInternalControl((prev) => {
                    const next = {
                        ...prev,
                        [manualKey]: Array.from(new Set([...prev[manualKey], ...ids])),
                        [excludedKey]: prev[excludedKey].filter((eId) => !ids.includes(eId)),
                    };
                    refreshFinalUserList(target, next[manualKey], null, next[excludedKey]);
                    return next;
                });
            } else {
                setProtocol((prev) => {
                    const next = { ...prev, [target]: Array.from(new Set([...(prev[target] || []), ...ids])) };
                    refreshFinalUserList(target === 'viewersClassroom' ? 'viewersUser' : 'answersViewersUser', null, next[target], null);
                    return next;
                });
            }
        }
    };

    const FilterPill = ({ label, active, onClick, color }) => (
        <button
            onClick={onClick}
            className="btn rounded-pill border-0 shadow-sm filter-pill-btn px-3 py-1"
            style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                backgroundColor: active ? color : '#f0f0f0',
                color: active ? '#fff' : '#535353',
                boxShadow: active ? `0 4px 10px ${color}44` : 'none',
                opacity: active ? 1 : 0.7,
            }}
        >
            {label}
        </button>
    );

    const renderSelectionList = (target, label, type = 'user') => {
        const isRestrict =
            (target.includes('viewer') && protocol.visibility === 'RESTRICT') ||
            (target.includes('answer') && protocol.answersVisibility === 'RESTRICT') ||
            (target === 'appliers' && protocol.applicability === 'RESTRICT');
        if (!isRestrict) return null;

        const suffix = target.charAt(0).toUpperCase() + target.slice(1);
        const manualKey = `manual${suffix}`;
        const excludedKey = `excluded${suffix}`;
        const classroomKey = target === 'viewersUser' ? 'viewersClassroom' : 'answersViewersClassroom';

        return (
            <fieldset className="mb-4 animate-fade-in border p-3 rounded-4 bg-light bg-opacity-10">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                    <p className="form-label color-steel-blue fs-6 fw-medium mb-0">{label}</p>
                    <div className="d-flex gap-2">
                        <FilterPill label="Selecionar todos" active onClick={() => bulkAction('ALL', target)} color="#4E9BB9" />
                        <FilterPill label="Limpar selecionados" active onClick={() => bulkAction('CLEAR', target)} color="#F59489" />
                    </div>
                </div>
                <input
                    type="text"
                    placeholder="Buscar..."
                    className="form-control form-control-sm search-input fw-medium rounded-4 border border-1 border-secondary-subtle py-2 px-3 mb-3"
                    value={searchInputs[target] || ''}
                    onChange={(e) => setSearchInputs((p) => ({ ...p, [target]: e.target.value }))}
                />

                <div className="row gy-2 px-2 create-page-custom-scroll" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {filteredLists[target].map((item) => {
                        let isChecked, isFromGroup, isExcluded;
                        if (type === 'user') {
                            isFromGroup = (protocol[classroomKey] || []).some((cId) =>
                                masterPool.classrooms.find((g) => g.id === cId)?.users?.some((u) => u.id === item.id)
                            );
                            isExcluded = internalControl[excludedKey].includes(item.id);
                            isChecked = (internalControl[manualKey].includes(item.id) || isFromGroup) && !isExcluded;
                        } else {
                            isChecked = (protocol[target] || []).includes(item.id);
                        }

                        return (
                            <div key={`${target}-${item.id}`} className="col-6 col-md-4 col-lg-3 d-flex align-items-center form-check mb-1">
                                <input
                                    type="checkbox"
                                    className="form-check-input cursor-pointer"
                                    checked={isChecked}
                                    onChange={() =>
                                        type === 'user' ? toggleUserSelection(item.id, target) : toggleClassroom(item.id, target)
                                    }
                                />
                                <label
                                    className={`ms-2 fs-6 text-truncate cursor-pointer ${
                                        isChecked ? 'color-grey fw-medium' : 'text-muted '
                                    }`}
                                    onClick={() =>
                                        type === 'user' ? toggleUserSelection(item.id, target) : toggleClassroom(item.id, target)
                                    }
                                >
                                    {item.username || item.name}
                                    {isFromGroup && !isExcluded && (
                                        <small className="d-block text-info" style={{ fontSize: '0.6rem' }}>
                                            (via grupo)
                                        </small>
                                    )}
                                </label>
                            </div>
                        );
                    })}
                </div>
            </fieldset>
        );
    };

    return (
        <div className="create-page-custom-scroll overflow-y-auto mb-3 pe-3">
            {/* Título e Descrição */}
            <label htmlFor="title" className="form-label color-steel-blue fs-5 fw-medium me-1">
                Título do protocolo
            </label>
            <MaterialSymbol
                icon="question_mark"
                size={13}
                weight={700}
                fill
                color="#FFFFFF"
                dataBsToggle="tooltip"
                dataBsCustomClass="title-tooltip"
                dataBsTitle="Texto curto que identifique o protocolo com no máximo 255 caracteres."
                className="bg-steel-blue title-tooltip p-1 rounded-circle"
            />
            <input
                className="form-control rounded-4 pastel-blue-input border border-1 border-steel-blue fs-6 mb-3"
                id="title"
                type="text"
                value={protocol.title || ''}
                onChange={(event) => setProtocol((prev) => ({ ...prev, title: event.target.value }))}
                minLength="3"
                required
            ></input>
            <label htmlFor="description" className="form-label color-steel-blue fs-5 fw-medium me-1">
                Descrição do protocolo
            </label>
            <MaterialSymbol
                icon="question_mark"
                size={13}
                weight={700}
                fill
                color="#FFFFFF"
                dataBsToggle="tooltip"
                dataBsCustomClass="description-tooltip"
                dataBsTitle="Texto que descreva a finalidade do protocolo e/ou instruções para sua execução. Suporta Markdown com até 3000 caracteres."
                className="bg-steel-blue description-tooltip p-1 rounded-circle"
            />
            <textarea
                className="form-control rounded-4 pastel-blue-input border border-1 border-steel-blue fs-6 mb-3"
                id="description"
                rows="4"
                value={protocol.description || ''}
                onChange={(event) => setProtocol((prev) => ({ ...prev, description: event.target.value }))}
            ></textarea>

            {/* Replicabilidade do Protocolo */}
            <div className="form-check form-switch fs-5 mb-2">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="replicable"
                    checked={protocol.replicable}
                    onChange={(event) => setProtocol((prev) => ({ ...prev, replicable: !prev.replicable }))}
                />
                <label className="form-check-label color-steel-blue fs-5 fw-medium me-2" htmlFor="enabled">
                    Replicável
                </label>
                <MaterialSymbol
                    icon="question_mark"
                    size={13}
                    weight={700}
                    fill
                    color="#FFFFFF"
                    dataBsToggle="tooltip"
                    dataBsCustomClass="replicable-tooltip"
                    dataBsTitle="Se os aplicadores poderão criar réplicas do protocolo. Uma réplica possui os mesmos itens e estrutura do protocolo original, mas com regras (acesso, replicabilidade, etc) definidas pelo usuário que replicou, sem controle do criador original. A réplica não acompanhará futuras alterações deste protocolo."
                    className="bg-steel-blue replicable-tooltip p-1 rounded-circle"
                />
            </div>

            {/* Visibilidade do Protocolo */}
            <label htmlFor="visibility" className="form-label color-steel-blue fs-5 fw-medium me-2">
                Visibilidade do protocolo
            </label>
            <MaterialSymbol
                icon="question_mark"
                size={13}
                weight={700}
                fill
                color="#FFFFFF"
                dataBsToggle="tooltip"
                dataBsCustomClass="visibility-tooltip"
                dataBsTitle="Quem poderá visualizar o protocolo. Um protocolo público é acessível para todos os usuários, enquanto o protocolo restrito só é acessível para os usuários e grupos que você selecionar. Aplicações definidas como mais restritas podem não ser acessíveis para todos os usuários definidos aqui."
                className="bg-steel-blue visibility-tooltip p-1 rounded-circle"
            />
            <select
                className="form-select rounded-4 pastel-blue-input fw-medium fs-6 mb-3"
                id="visibility"
                value={protocol.visibility || ''}
                onChange={(event) => setProtocol((prev) => ({ ...prev, visibility: event.target.value }))}
                required
            >
                <option value="">Selecione...</option>
                <option value="PUBLIC">Público (inclui visitantes)</option>
                <option value="AUTHENTICATED">Autenticado (usuários registrados)</option>
                <option value="PRIVATE">Privado (somente você e administradores)</option>
                <option value="RESTRICT">Restrito (usuários e grupos selecionados)</option>
            </select>
            {renderSelectionList('viewersUser', 'Selecione os usuários que poderão visualizar o protocolo:', 'user')}
            {renderSelectionList('viewersClassroom', 'Selecione os grupos que poderão visualizar o protocolo:', 'group')}

            {/* Aplicabilidade do Protocolo */}
            <label htmlFor="applicability" className="form-label color-steel-blue fs-5 fw-medium me-2">
                Aplicabilidade
            </label>
            <MaterialSymbol
                icon="question_mark"
                size={13}
                weight={700}
                fill
                color="#FFFFFF"
                dataBsToggle="tooltip"
                dataBsCustomClass="applicability-tooltip"
                dataBsTitle="Quem poderá aplicar o protocolo. Um protocolo público é aplicável por todos os usuários, enquanto o protocolo restrito só é aplicável pelos usuários e grupos que você selecionar. A aplicação herda as restrições de visibilidade do protocolo, mas pode ser definida para ser ainda mais restrita. O criador do protocolo terá acesso às respostas de todas as aplicações."
                className="bg-steel-blue applicability-tooltip p-1 rounded-circle"
            />
            <select
                className="form-select rounded-4 pastel-blue-input fw-medium fs-6 mb-3"
                id="applicability"
                value={protocol.applicability || ''}
                onChange={(event) => setProtocol((prev) => ({ ...prev, applicability: event.target.value }))}
                required
            >
                <option value="">Selecione...</option>
                <option value="AUTHENTICATED">Autenticado (aplicadores registrados)</option>
                <option value="PRIVATE">Privado (somente você e administradores)</option>
                <option value="RESTRICT">Restrito (aplicadores selecionados)</option>
            </select>
            {renderSelectionList('appliers', 'Selecione os usuários que poderão aplicar o protocolo:', 'user')}

            {/* Visibilidade das respostas do Protocolo */}
            <label htmlFor="answer-visiblity" className="form-label color-steel-blue fs-5 fw-medium me-2">
                Visibilidade das respostas
            </label>
            <MaterialSymbol
                icon="question_mark"
                size={13}
                weight={700}
                fill
                color="#FFFFFF"
                dataBsToggle="tooltip"
                dataBsCustomClass="answer-visiblity-tooltip"
                dataBsTitle="Quem poderá visualizar as respostas do protocolo. Um protocolo público permite que todos os usuários vejam suas respostas, enquanto o protocolo restrito só permite que os usuários e grupos que você selecionar vejam as respostas. Respostas de aplicações definidas como mais restritas que o protocolo não serão visíveis por todos os usuários definidos aqui."
                className="bg-steel-blue answer-visiblity-tooltip p-1 rounded-circle"
            />
            <select
                className="form-select rounded-4 pastel-blue-input fw-medium fs-6 mb-3"
                id="answer-visiblity"
                value={protocol.answersVisibility || ''}
                onChange={(event) => setProtocol((prev) => ({ ...prev, answersVisibility: event.target.value }))}
                required
            >
                <option value="">Selecione...</option>
                <option value="PUBLIC">Público (inclui visitantes)</option>
                <option value="AUTHENTICATED">Autenticado (usuários registrados)</option>
                <option value="PRIVATE">Privado (somente você e administradores)</option>
                <option value="RESTRICT">Restrito (usuários e grupos selecionados)</option>
            </select>
            {renderSelectionList('answersViewersUser', 'Selecione os usuários que poderão visualizar as respostas do protocolo:', 'user')}
            {renderSelectionList(
                'answersViewersClassroom',
                'Selecione os grupos que poderão visualizar as respostas do protocolo:',
                'group'
            )}
            <style>{CreateProtocolPropertiesStyles}</style>
        </div>
    );
}

export default CreateProtocolProperties;
